import { Controller } from "@hotwired/stimulus"

// Apple Pay button for balance due payments on the account order page.
// Simpler than the cart Apple Pay flow — no shipping selection needed.
export default class extends Controller {
  static values = {
    amount: String,
    storeName: String,
    sessionPath: String,
    payPath: String
  }

  connect() {
    if (window.ApplePaySession && ApplePaySession.canMakePayments()) {
      this.element.classList.remove("d-none")
    }
  }

  pay(event) {
    event.preventDefault()

    if (this.element.dataset.disabled === "true") return
    this.element.dataset.disabled = "true"

    const request = {
      countryCode: "US",
      currencyCode: "USD",
      supportedNetworks: ["visa", "masterCard", "amex", "discover"],
      merchantCapabilities: ["supports3DS"],

      requiredBillingContactFields: [
        "postalAddress",
        "name"
      ],

      total: {
        type: "final",
        label: this.storeNameValue,
        amount: this.amountValue
      }
    }

    const session = new ApplePaySession(3, request)

    session.onvalidatemerchant = (event) => {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

      fetch(this.sessionPathValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken
        },
        body: `url=${encodeURIComponent(event.validationURL)}`
      })
        .then(res => res.json())
        .then(data => session.completeMerchantValidation(data))
        .catch(() => {
          session.abort()
          this.element.dataset.disabled = "false"
        })
    }

    session.onpaymentauthorized = (event) => {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const billing = event.payment.billingContact

      const formData = new URLSearchParams()
      formData.append("payment_method", "apple_pay")
      formData.append("dataValue", btoa(JSON.stringify(event.payment.token.paymentData)))
      formData.append("billing_address[first_name]", billing.givenName || "")
      formData.append("billing_address[last_name]", billing.familyName || "")
      formData.append("billing_address[address]", (billing.addressLines || [])[0] || "")
      formData.append("billing_address[address2]", (billing.addressLines || [])[1] || "")
      formData.append("billing_address[city]", billing.locality || "")
      formData.append("billing_address[region]", billing.administrativeArea || "")
      formData.append("billing_address[postal_code]", billing.postalCode || "")
      formData.append("billing_address[country]", billing.countryCode || "US")

      fetch(this.payPathValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken
        },
        body: formData.toString(),
        redirect: "follow"
      })
        .then(res => {
          if (res.redirected) {
            // Apple Pay only allows ~30s between authorization and
            // completePayment; a slow charge means the sheet has already timed
            // out and this call throws on the dead session. The payment still
            // went through server-side, so redirect anyway — letting the throw
            // escape would land in .catch and falsely report a failure.
            try {
              session.completePayment(ApplePaySession.STATUS_SUCCESS)
            } catch (err) {}
            window.location = res.url
          } else {
            try {
              session.completePayment(ApplePaySession.STATUS_FAILURE)
            } catch (err) {}
            this.element.dataset.disabled = "false"
            alert("There was a problem processing your payment. Please try the credit card form below.")
          }
        })
        .catch(() => {
          try {
            session.completePayment(ApplePaySession.STATUS_FAILURE)
          } catch (err) {}
          this.element.dataset.disabled = "false"
          alert("There was a problem processing your payment. Please try the credit card form below.")
        })
    }

    session.oncancel = () => {
      this.element.dataset.disabled = "false"
    }

    session.begin()
  }
}
